/* eslint-disable react/no-children-prop */
import {
  Typography,
  Box,
  MenuList,
  MenuItem,
  Divider,
  Skeleton,
} from '@mui/material';
import Header from '@jobseeker/lotus/components/Header';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import type {Props} from '@jobseeker/lotus/components/Header/types';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import Avatar from '@jobseeker/lotus/components/Avatar';
import {getOldLink} from '@jobseeker/lotus/utils';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  ChevronDownLight,
  Edit,
  Login,
  Logout,
} from '@jobseeker/lotus/assets/icons';
import Drop from '@jobseeker/lotus/components/Drop/Drop';
import useInfoBannerHeight from '@jobseeker/common-core/hooks/useInfoBannerHeight';
import {SyntheticEvent, useState} from 'react';
import {convertDate, openUrl} from '@jobseeker/common-core/utils/common';

import type {} from '@jobseeker/lotus/mui.types';
import dayjs from 'dayjs';

const HeaderWithProfile = (props: Props) => {
  const theme = useWrappedTheme();

  const [openDrop, setDropOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const bannerHeight = useInfoBannerHeight();

  const handleDropOpen = (event: React.BaseSyntheticEvent) => {
    const {currentTarget} = event;
    setDropOpen(true);
    setAnchorEl(currentTarget);
  };

  const handleDropClose = () => {
    setDropOpen(false);
    setAnchorEl(null);
  };

  const {
    profile: {email, name, picUrl},
  } = useSelector((state: AppState) => state.user);

  const {isLoggedIn} = useSelector((state: AppState) => state.auth);
  const page = useSelector((state: AppState) => state.app.page);

  const {
    timelines: {
      registration: {endDate},
      config: {competitionName},
    },
  } = useSelector((state: AppState) => state.competition);

  const menuItems = [
    {
      label: 'Edit Profile',
      icon: Edit,
      handleClick: (event: SyntheticEvent) => {
        event?.preventDefault();
        openUrl(getOldLink('/registration/addPersonalDetails'), '_blank');
      },
    },
    {
      label: 'Logout',
      icon: Logout,
      handleClick: (event: SyntheticEvent) => {
        event?.preventDefault();
        props.OnLogoutBtnClick && props.OnLogoutBtnClick();
      },
    },
  ];

  const scrollToId = (id: string) => {
    const offSet = document?.getElementById(id)?.offsetTop;
    if (offSet) {
      window?.scrollTo({behavior: 'smooth', top: offSet - 70});
    }
  };

  return (
    <Header
      registrationEndDate={
        endDate && dayjs(endDate) >= dayjs()
          ? convertDate(endDate, 'YYYY-MM-DD HH:mm:ss', 'Do MMM YY')
          : ''
      }
      {...props}
    >
      <Box sx={{display: 'flex', marginLeft: 'auto', alignItems: 'center'}}>
        {isLoggedIn && (
          <>
            {!!email ? (
              <Box
                display="flex"
                mr={1}
                alignItems={'center'}
                onMouseLeave={handleDropClose}
              >
                <Avatar
                  sx={{width: 40, height: 40}}
                  imgUrl={
                    picUrl ? picUrl : `${getOldLink('/resources/img/user.jpg')}`
                  }
                />
                <Box display={'block'} ml={1}>
                  <Typography variant="subtitle1" mb={-1}>
                    {name}
                  </Typography>
                  <Typography variant="body3">{email}</Typography>
                </Box>
                <Box ml={2} onMouseEnter={handleDropOpen}>
                  <Image
                    src={ChevronDownLight}
                    alt={'open_nav_category'}
                    width={16}
                    height={16}
                    objectFit="cover"
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Drop
                  open={openDrop}
                  bannerHeight={bannerHeight}
                  anchorEl={anchorEl}
                  handleMenuClose={handleDropClose}
                  theme={theme}
                  title={''}
                  children={
                    <MenuList>
                      {menuItems.map((item, index) => (
                        <MenuItem
                          key={`header-profile-nav-${index}`}
                          onClick={event => {
                            console.log(
                              'Event Click',
                              item.label,
                              item.handleClick
                            );
                            item.handleClick(event);
                          }}
                          sx={{
                            backgroundColor: '#fff',
                            ':hover': {
                              zIndex: 2,
                              backgroundColor: '#fff',
                            },
                            ':focus': {
                              backgroundColor: '#fff',
                            },
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            padding: 0,
                            display: 'block',
                            borderBottom:
                              index > 0
                                ? ''
                                : '1px solid rgba(126, 126, 126, 0.2)',
                          }}
                        >
                          <Box
                            display={'flex'}
                            py={theme.spacing(2)}
                            px={theme.spacing(3)}
                          >
                            <Image
                              src={item.icon}
                              alt={'open_nav_category'}
                              width={16}
                              height={16}
                              objectFit="cover"
                              style={{
                                cursor: 'pointer',
                              }}
                            />
                            <Typography variant="body2" ml={2}>
                              {item.label}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </MenuList>
                  }
                  count={0}
                />
              </Box>
            ) : (
              <Skeleton
                variant="rounded"
                width={theme.spacing(4.26)}
                height={theme.spacing(4.26)}
                sx={{
                  ml: theme.spacing(1.195),
                  display: {xxs: 'none', md: 'block'},
                }}
              />
            )}
          </>
        )}

        {!isLoggedIn && (
          <>
            <Box display={'inline-flex'} alignItems={'center'}>
              <Image
                src={Login}
                alt={'Login_Alt'}
                width={18}
                height={18}
                objectFit="cover"
                quality={50}
                onClick={() => {}}
                style={{
                  margin: theme.spacing(0.875),
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  cursor: 'pointer',
                }}
                onClick={event => {
                  event?.stopPropagation;
                  props.onLoginClick && props.onLoginClick(event);
                }}
              >
                Login
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              variant="middle"
              sx={{
                border: '0.60px #333333 solid',
                mx: `${theme.spacing(2.5)} !important`,
                height: 20,
              }}
              flexItem
            />
            <Typography
              variant="subtitle1"
              onClick={() => {
                if (page === 'epoch-register') {
                  scrollToId('RegistrationStarts');
                } else {
                  openUrl(`/${competitionName}/register`, '_self');
                }
              }}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'secondary.main',
              }}
            >
              Register
            </Typography>
          </>
        )}
      </Box>
    </Header>
  );
};

export default HeaderWithProfile;
